import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  sidebarOpen: true,
  theme: 'light', // 'light' or 'dark'
  notifications: [],
  loading: {
    global: false,
    bookings: false,
    prices: false,
  },
  modals: {
    addBooking: false,
    editBooking: false,
    addPrice: false,
    editPrice: false,
    deleteConfirm: false,
  },
  selectedItems: {
    bookings: [],
    prices: [],
  },
  filters: {
    bookings: {
      status: 'all',
      dateRange: null,
      search: '',
    },
    prices: {
      category: 'all',
      search: '',
    },
  },
  pagination: {
    bookings: {
      page: 1,
      limit: 10,
      total: 0,
    },
    prices: {
      page: 1,
      limit: 10,
      total: 0,
    },
  },
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Sidebar actions
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action) => {
      state.sidebarOpen = action.payload;
    },

    // Theme actions
    setTheme: (state, action) => {
      state.theme = action.payload;
      localStorage.setItem('theme', action.payload);
    },

    // Notification actions
    addNotification: (state, action) => {
      const notification = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        ...action.payload,
      };
      state.notifications.unshift(notification);
      // Keep only last 10 notifications
      if (state.notifications.length > 10) {
        state.notifications = state.notifications.slice(0, 10);
      }
    },
    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload
      );
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },

    // Loading actions
    setLoading: (state, action) => {
      const { key, value } = action.payload;
      state.loading[key] = value;
    },
    setGlobalLoading: (state, action) => {
      state.loading.global = action.payload;
    },

    // Modal actions
    openModal: (state, action) => {
      state.modals[action.payload] = true;
    },
    closeModal: (state, action) => {
      state.modals[action.payload] = false;
    },
    closeAllModals: (state) => {
      Object.keys(state.modals).forEach((key) => {
        state.modals[key] = false;
      });
    },

    // Selection actions
    selectItem: (state, action) => {
      const { type, id } = action.payload;
      if (!state.selectedItems[type].includes(id)) {
        state.selectedItems[type].push(id);
      }
    },
    deselectItem: (state, action) => {
      const { type, id } = action.payload;
      state.selectedItems[type] = state.selectedItems[type].filter(
        (itemId) => itemId !== id
      );
    },
    selectAllItems: (state, action) => {
      const { type, ids } = action.payload;
      state.selectedItems[type] = ids;
    },
    clearSelection: (state, action) => {
      const type = action.payload;
      state.selectedItems[type] = [];
    },

    // Filter actions
    setFilter: (state, action) => {
      const { type, filter, value } = action.payload;
      state.filters[type][filter] = value;
    },
    clearFilters: (state, action) => {
      const type = action.payload;
      if (type === 'bookings') {
        state.filters.bookings = {
          status: 'all',
          dateRange: null,
          search: '',
        };
      } else if (type === 'prices') {
        state.filters.prices = {
          category: 'all',
          search: '',
        };
      }
    },

    // Pagination actions
    setPagination: (state, action) => {
      const { type, pagination } = action.payload;
      state.pagination[type] = { ...state.pagination[type], ...pagination };
    },
    resetPagination: (state, action) => {
      const type = action.payload;
      state.pagination[type] = {
        page: 1,
        limit: 10,
        total: 0,
      };
    },

    // Reset all UI state
    resetUI: () => initialState,
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  setTheme,
  addNotification,
  removeNotification,
  clearNotifications,
  setLoading,
  setGlobalLoading,
  openModal,
  closeModal,
  closeAllModals,
  selectItem,
  deselectItem,
  selectAllItems,
  clearSelection,
  setFilter,
  clearFilters,
  setPagination,
  resetPagination,
  resetUI,
} = uiSlice.actions;

export default uiSlice.reducer;
